import { ChangeEvent, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { company } from '~/company/Company';
import InlineNotification from '~/components/InlineNotification/InlineNotification';
import { Button } from '~/components/isolated/Button';
import { Checkbox } from '~/components/isolated/Checkbox';
import { ModalFooter } from '~/components/Modal/components/ModalFooter';
import { useGlobal } from '~/hooks/useGlobal';

import { AuthHeader } from '../../components/AuthHeader';
import { PhoneInput } from '../../components/PhoneInput';

import styles from './PhoneView.module.scss';

/** URLs for legal documents (terms and privacy policy) */
const TERMS_URLS = [
  company.config.links.legals?.generalTradingPolicy || { link: '' },
  company.config.links.legals?.privacyPolicy || { link: '' },
];

/**
 * Props for the PhoneView component.
 */
interface PhoneViewProps {
  /** Current phone number value */
  phoneNumber: string;
  /** Callback fired when phone number changes */
  onPhoneChange: (value: string) => void;
  /** Callback fired when phone validation status changes */
  onPhoneValidate: (isValid: boolean) => void;
  /** Whether the terms agreement checkbox is checked */
  agreementChecked: boolean;
  /** Callback fired when agreement checkbox changes */
  onAgreementChange: (checked: boolean) => void;
  /** Error message for phone input */
  phoneError: string | null;
  /** Whether to show agreement validation error */
  agreementError: boolean;
  /** Callback to close the agreement error notification */
  onAgreementErrorClose: () => void;
  /** Callback to submit the phone form */
  onSubmit: () => void;
  /** Callback to go back/close the modal */
  onBack: () => void;
  /** Whether a request is in progress */
  isLoading: boolean;
  /** Whether the submit button should be disabled */
  isSubmitDisabled: boolean;
}

/**
 * Phone number entry view for the authentication modal.
 * Includes phone input, terms agreement checkbox, and submit button.
 */
const PhoneView = ({
  phoneNumber,
  onPhoneChange,
  onPhoneValidate,
  agreementChecked,
  onAgreementChange,
  phoneError,
  agreementError,
  onAgreementErrorClose,
  onSubmit,
  onBack,
  isLoading,
  isSubmitDisabled,
}: PhoneViewProps) => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();

  const handleCheckboxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onAgreementChange(e.target.checked);
    },
    [onAgreementChange],
  );

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <AuthHeader
          title={t('phrases:loginOrRegister')}
          subtitle={t('phrases:enterPhoneNumber')}
          onClose={onBack}
        />

        <PhoneInput
          value={phoneNumber}
          onChange={onPhoneChange}
          onValidate={onPhoneValidate}
          error={phoneError}
        />

        <Checkbox
          className={styles.agreement}
          checked={agreementChecked}
          onChange={handleCheckboxChange}
          size={24}
          label={
            <Trans
              i18nKey="phrases:agreeToCollection"
              components={TERMS_URLS.map(({ link = '' }) => (
                <Link to={link} key={link} />
              ))}
            />
          }
          classes={{
            label: styles.agreementLabel,
          }}
        />

        {agreementError && (
          <InlineNotification
            variant="error"
            title={t('errors:agreementRequired')}
            onClose={onAgreementErrorClose}
            classes={{
              root: styles.agreementError,
              title: styles.agreementErrorTitle,
            }}
          />
        )}

        <Button
          variant="primary"
          size="XL"
          onClick={onSubmit}
          disabled={isSubmitDisabled}
          isLoading={isLoading}
          className={styles.desktopSubmitButton}
        >
          {t('verifyNumber')}
        </Button>
      </div>

      {isMobile && (
        <ModalFooter
          onBack={onBack}
          primaryAction={{
            text: t('verifyNumber'),
            onClick: onSubmit,
            disabled: isSubmitDisabled,
            isLoading,
          }}
        />
      )}
    </div>
  );
};

PhoneView.displayName = 'PhoneView';

export default PhoneView;
